import * as React from "react"
import { AnimatePresence, motion } from "framer-motion"
import { C, FONT, R, S, EASE, SPRING, T } from "./tokens"

// ═══════════════════════════════════════════════════════════════════════════
// PRIMITIVES
//
// The single biggest gap in the current build is that ~40 controls are bare
// <button> with no press state. One primitive fixes all of them at once, and
// makes it impossible for the next control to ship without one.
// ═══════════════════════════════════════════════════════════════════════════

export const useReduced = () =>
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

/** Every tappable thing in the app.
 *
 *  Scale plus a brightness lift, on the existing SPRING. 0.97 rather than the
 *  0.92 used in a few places today — at card size 0.92 reads as the element
 *  shrinking, at chip size it reads as a press. One value that works at both
 *  is better than two that each work at one. */
export function Press({
    as = "button",
    children,
    className = "",
    style,
    onClick,
    disabled,
    scale = 0.97,
    ...rest
}: any) {
    const reduced = useReduced()
    const M = as === "div" ? motion.div : as === "a" ? motion.a : motion.button
    return (
        <M
            className={className}
            style={style}
            onClick={disabled ? undefined : onClick}
            disabled={as === "button" ? disabled : undefined}
            whileTap={reduced || disabled ? undefined : { scale, filter: "brightness(1.06)" }}
            transition={SPRING}
            {...rest}
        >
            {children}
        </M>
    )
}

/** A figure that counts to its value on first paint.
 *
 *  Restricted to the hero balance. Animating every number would be noise;
 *  animating the one number the user opened the app to see reads as the app
 *  arriving rather than the page loading. Skipped entirely under reduced
 *  motion, and skipped when the value is masked. */
export function CountUp({
    value,
    format,
    active = true,
}: {
    value: number
    format: (n: number) => string
    active?: boolean
}) {
    const reduced = useReduced()
    const [shown, setShown] = React.useState(reduced || !active ? value : 0)
    React.useEffect(() => {
        if (reduced || !active) { setShown(value); return }
        let raf = 0
        const start = performance.now()
        const dur = 900
        const from = 0
        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / dur)
            // Same curve as EASE, so the number lands with everything else.
            const e = 1 - Math.pow(1 - t, 3)
            setShown(from + (value - from) * e)
            if (t < 1) raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [value, active, reduced])
    return <>{format(shown)}</>
}

/** Skeletons shaped like the content they stand in for.
 *
 *  A spinner tells the user to wait; a skeleton tells them what is coming and
 *  keeps the layout from jumping when it arrives. */
export function Skeleton({ w, h, r = R.sm, style }: { w?: number | string; h: number; r?: number; style?: any }) {
    return <div className="sk" style={{ width: w ?? "100%", height: h, borderRadius: r, ...style }} />
}

/** Toast — bottom on mobile, above the dock; top-right on desktop. */
export function Toast({ msg }: { msg: string | null }) {
    return (
        <AnimatePresence>
            {msg && (
                <motion.div
                    className="toast g g-dark"
                    initial={{ opacity: 0, y: 14, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={SPRING}
                    role="status"
                >
                    <Icon name="check" size={16} color={C.mint} stroke={2.4} />
                    {msg}
                </motion.div>
            )}
        </AnimatePresence>
    )
}

// ── Icons — Lucide geometry, stroke 1.5, sizes 16/20/24 ────────────────────
export type IconName =
    | "home" | "swap" | "card" | "clock" | "user" | "eye" | "eyeOff" | "plus"
    | "arrowUp" | "arrowDown" | "arrowLeft" | "arrowRight" | "check" | "x"
    | "zap" | "wallet" | "search" | "chevronRight" | "copy" | "send" | "filter"
    | "receipt" | "shield" | "bell" | "moreH" | "download" | "globe" | "chevronLeft"
    | "chevronDown" | "info" | "flag" | "percent" | "star" | "alert" | "minus"
    | "circleCheck" | "circleX" | "circleDashed" | "loader" | "hourglass"
    | "share" | "message"

export function Icon({
    name, size = 20, color = "currentColor", stroke = 1.5,
}: { name: IconName; size?: number; color?: string; stroke?: number }) {
    const p = {
        width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color,
        strokeWidth: stroke, strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
        "aria-hidden": true, focusable: false,
    }
    switch (name) {
        case "home": return <svg {...p}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" /><path d="M9.5 21v-6h5v6" /></svg>
        case "swap": return <svg {...p}><path d="m17 3 4 4-4 4" /><path d="M21 7H8" /><path d="m7 21-4-4 4-4" /><path d="M3 17h13" /></svg>
        case "card": return <svg {...p}><rect x="2" y="5" width="20" height="14" rx="3" /><path d="M2 10h20" /><path d="M6 15h4" /></svg>
        case "clock": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 1.8" /></svg>
        case "user": return <svg {...p}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
        case "eye": return <svg {...p}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
        case "eyeOff": return <svg {...p}><path d="M10.7 5.1A10.4 10.4 0 0 1 12 5c7 0 10 7 10 7a13 13 0 0 1-1.7 2.7" /><path d="M6.6 6.6A13.5 13.5 0 0 0 2 12s3 7 10 7a9.7 9.7 0 0 0 5.4-1.6" /><path d="m2 2 20 20" /><path d="M14.1 14.1a3 3 0 1 1-4.2-4.2" /></svg>
        case "plus": return <svg {...p}><path d="M5 12h14" /><path d="M12 5v14" /></svg>
        case "arrowUp": return <svg {...p}><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></svg>
        case "arrowDown": return <svg {...p}><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></svg>
        case "arrowLeft": return <svg {...p}><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>
        case "arrowRight": return <svg {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
        case "check": return <svg {...p}><path d="M20 6 9 17l-5-5" /></svg>
        case "x": return <svg {...p}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        case "zap": return <svg {...p}><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>
        case "wallet": return <svg {...p}><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></svg>
        case "search": return <svg {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        case "chevronRight": return <svg {...p}><path d="m9 18 6-6-6-6" /></svg>
        case "copy": return <svg {...p}><rect x="8" y="8" width="14" height="14" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
        case "send": return <svg {...p}><path d="M14.5 21.7a.5.5 0 0 0 .9 0l6.5-19a.5.5 0 0 0-.6-.6l-19 6.5a.5.5 0 0 0 0 .9l7.9 3.2a2 2 0 0 1 1.1 1.1z" /><path d="M21.9 2.2 11 13.1" /></svg>
        case "filter": return <svg {...p}><path d="M3 5h18" /><path d="M6 12h12" /><path d="M10 19h4" /></svg>
        case "download": return <svg {...p}><path d="M12 3v11" /><path d="m7.5 10.5 4.5 4.5 4.5-4.5" /><path d="M4 19h16" /></svg>
        case "globe": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" /></svg>
        case "chevronLeft": return <svg {...p}><path d="m15 18-6-6 6-6" /></svg>
        case "chevronDown": return <svg {...p}><path d="m6 9 6 6 6-6" /></svg>
        case "minus": return <svg {...p}><path d="M5 12h14" /></svg>
        case "alert": return <svg {...p}><path d="M12 6.5v7" /><path d="M12 17.4h.01" /></svg>
        /* Tracker stage glyphs. Each state is carried by SHAPE as well as
           colour — a checkmark, a dashed ring, an arc, an hourglass, a cross —
           so the timeline still reads with colour vision differences or on a
           washed-out screen in daylight. */
        case "circleCheck": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M8.4 12.2l2.5 2.5 4.7-4.9" /></svg>
        case "circleX": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M14.9 9.1l-5.8 5.8" /><path d="M9.1 9.1l5.8 5.8" /></svg>
        case "circleDashed": return (
            <svg {...p}><circle cx="12" cy="12" r="9" strokeDasharray="3.1 3.6" /></svg>
        )
        case "loader": return <svg {...p}><path d="M20.5 12a8.5 8.5 0 1 0-3.1 6.56" /></svg>
        case "hourglass": return (
            <svg {...p}><path d="M7 3h10" /><path d="M7 21h10" />
                <path d="M8 3v3.5L12 11l4-4.5V3" /><path d="M8 21v-3.5L12 13l4 4.5V21" /></svg>
        )
        case "share": return (
            <svg {...p}><circle cx="18" cy="5.5" r="2.6" /><circle cx="6" cy="12" r="2.6" />
                <circle cx="18" cy="18.5" r="2.6" />
                <path d="M8.3 10.8l7.4-4" /><path d="M8.3 13.2l7.4 4" /></svg>
        )
        case "message": return (
            <svg {...p}><path d="M20.5 11.7a7.8 7.8 0 0 1-8.4 7.77c-.6-.04-1.18-.14-1.74-.3L4.5 21l1.1-4.6a7.6 7.6 0 0 1-.6-3.03A7.8 7.8 0 0 1 12.8 4a7.8 7.8 0 0 1 7.7 7.7z" /></svg>
        )
        case "percent": return <svg {...p}><path d="m19 5-14 14" /><circle cx="7.5" cy="7.5" r="2.6" /><circle cx="16.5" cy="16.5" r="2.6" /></svg>
        case "star": return <svg {...p}><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" /></svg>
        case "flag": return <svg {...p}><path d="M4 21V4" /><path d="M4 4.5h11l-1.6 3.6L15 12H4" /></svg>
        case "info": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></svg>
        case "receipt": return <svg {...p}><path d="M4 2v20l2.5-1.5L9 22l2.5-1.5L14 22l2.5-1.5L19 22V2l-2.5 1.5L14 2l-2.5 1.5L9 2 6.5 3.5z" /><path d="M8 8h8" /><path d="M8 12h6" /></svg>
        case "shield": return <svg {...p}><path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.7 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.5 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
        case "bell": return <svg {...p}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
        default: return <svg {...p}><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></svg>
    }
}
